import api from './api';
import type { ApiResponse } from '@/types/api.types';

// Types
export interface Schedule {
  id: string;
  name: string;
  source_path: string;
  source_paths?: string[];
  cron_expression: string;
  is_enabled: boolean;
  watcher_id?: string;
  watcher_name?: string; // QA #108: the bound watcher's name
  next_run_at: string;
  last_run_at?: string;
  created_at: string;
  options?: {
    include_hidden?: boolean;
    follow_symlinks?: boolean;
    max_file_size_mb?: number;
    exclude_patterns?: string[];
  };
}

export interface CreateScheduleRequest {
  name: string;
  source_path?: string;
  source_paths?: string[];
  cron_expression: string;
  watcher_id: string; // Required: backend CreateScheduleRequest binds watcher_id as required
  options?: {
    include_hidden?: boolean;
    follow_symlinks?: boolean;
    max_file_size_mb?: number;
    exclude_patterns?: string[];
  };
}

// Watcher ID is now selected from the UI (no more hardcoded default)

/**
 * Body of a 202 from POST /schedules/:id/trigger (ADR-044 D4): the schedule
 * is marked due and the bound watcher runs it on its next poll — no job
 * exists yet. A watcher that is offline / disabled / unbound answers 409
 * WATCHER_OFFLINE instead and next_run_at is left untouched.
 */
interface TriggerResponse {
  schedule_id: string;
  schedule_name: string;
  source_path: string;
  source_paths?: string[];
  watcher_id?: string | null;
  watcher_status: 'online' | 'degraded';
  triggered_at: string;
  message: string;
}

// Mock data
export const scheduleService = {
  /**
   * List all backup schedules
   */
  async listSchedules(params?: { user_id?: string }): Promise<Schedule[]> {

    const response = await api.get<ApiResponse<{ items: Schedule[]; pagination: any }>>('/api/v1/schedules', { params });
    const data = response.data.data;
    // Handle both { items: [...] } and direct array response
    if (Array.isArray(data)) {
      return data;
    }
    return data?.items || [];
  },

  /**
   * Create a new backup schedule
   */
  async createSchedule(data: CreateScheduleRequest): Promise<{ id: string; name: string; next_run_at: string }> {

    const requestData = { ...data };

    const response = await api.post<ApiResponse<{ id: string; name: string; next_run_at: string }>>(
      '/api/v1/schedules',
      requestData
    );
    return response.data.data!;
  },

  /**
   * Update a backup schedule
   */
  async updateSchedule(id: string, data: Partial<CreateScheduleRequest>): Promise<void> {

    // Backend UpdateScheduleRequest: every field optional; only what is sent changes.
    await api.put(`/api/v1/schedules/${id}`, data);
  },

  /**
   * Enable / disable a schedule (backend POST /schedules/:id/enable|disable).
   * Enable recomputes next_run_at from now; disable leaves it untouched.
   */
  async setScheduleEnabled(id: string, enabled: boolean): Promise<void> {
    await api.post(`/api/v1/schedules/${id}/${enabled ? 'enable' : 'disable'}`);
  },

  /**
   * Delete a backup schedule
   */
  async deleteSchedule(id: string): Promise<void> {

    await api.delete(`/api/v1/schedules/${id}`);
  },

  /**
   * Manually trigger a backup now
   */
  async triggerSchedule(id: string): Promise<TriggerResponse> {
    // skipGlobalError: the page turns 409 WATCHER_OFFLINE into a specific message.
    const response = await api.post<ApiResponse<{ message: string; trigger: TriggerResponse }>>(
      `/api/v1/schedules/${id}/trigger`,
      {},
      { skipGlobalError: true },
    );
    if (response.data.success && response.data.data?.trigger) return response.data.data.trigger;
    throw new Error('Failed to trigger schedule');
  },
};
